import { Check, MessagesSquare, FileText, ArrowRight, Repeat, Sparkles } from "lucide-react";
import AvatarMedia from "@/components/avatar/AvatarMedia";
import { publicAvatarDescription } from "@/lib/avatarUi";

// Reference visual 2 (FR25 / Story 5.4): big profile card with a hero portrait,
// factual public description + ✓ seal, two stats and a primary "Usar avatar" pill
// that opens the agent sheet (FR26). Used on the Dashboard highlight and the
// selection modal. `onSwap` renders the "trocar" affordance when >1 avatar.
export default function AvatarCardProfile({
  avatar,
  peerAvatars,
  onUseAvatar,
  onSwap,
  onGeneratePhoto,
  generating = false,
  readOnly = false,
}) {
  const stats = avatar.stats || {};
  const conversas = stats.conversas ?? 0;
  const conteudos = stats.conteudos ?? 0;
  const publicDescription = publicAvatarDescription(avatar, peerAvatars);

  return (
    <div
      data-testid="avatar-card-profile"
      className="relative flex flex-col rounded-2xl bg-brand-surface border border-brand-line overflow-hidden"
    >
      <div className="relative">
        <AvatarMedia
          avatar={avatar}
          peerAvatars={peerAvatars}
          aspectClass="aspect-square"
          roundedClass="rounded-none"
          monogramClass="text-[96px]"
          gradient
          generating={generating}
        />
        {onSwap && (
          <button
            type="button"
            data-testid="avatar-profile-swap"
            onClick={onSwap}
            className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-bg/70 backdrop-blur border border-brand-line text-brand-ink2 hover:text-brand-rose text-[12px] font-ui transition-colors min-h-[40px]"
          >
            <Repeat className="w-3.5 h-3.5" /> Trocar
          </button>
        )}
        {!readOnly && !avatar.foto_url && !generating && onGeneratePhoto && (
          <button
            type="button"
            data-testid="avatar-profile-generate-photo"
            onClick={() => onGeneratePhoto(avatar)}
            className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-bg/70 backdrop-blur border border-brand-rose/40 text-brand-rose text-[12px] font-ui hover:bg-brand-rose hover:text-white transition-colors min-h-[40px]"
          >
            <Sparkles className="w-3.5 h-3.5" /> Gerar retrato
          </button>
        )}
      </div>

      <div className="p-5 lg:p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-display text-2xl text-brand-ink truncate" data-testid="avatar-profile-publico">
            {publicDescription}
          </h3>
          <span className="shrink-0 w-5 h-5 rounded-full bg-brand-rose flex items-center justify-center" title="Avatar completo">
            <Check className="w-3 h-3 text-white" />
          </span>
        </div>
        <div className="flex items-center gap-5 mb-5" data-testid="avatar-profile-stats">
          <span className="inline-flex items-center gap-1.5 text-[13px] text-brand-ink2 font-ui">
            <MessagesSquare className="w-4 h-4 text-brand-rose" />
            <span className="text-brand-ink font-medium">{conversas}</span> conversas
          </span>
          <span className="inline-flex items-center gap-1.5 text-[13px] text-brand-ink2 font-ui">
            <FileText className="w-4 h-4 text-brand-rose" />
            <span className="text-brand-ink font-medium">{conteudos}</span> conteúdos
          </span>
        </div>

        {readOnly ? (
          <div className="mt-auto text-center text-[12.5px] text-brand-ink3 font-ui py-2">
            Somente leitura
          </div>
        ) : (
          <button
            type="button"
            data-testid="avatar-profile-use"
            onClick={() => onUseAvatar?.(avatar)}
            className="mt-auto w-full inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-5 py-3 rounded-full font-ui text-[14px] tracking-wide transition-colors min-h-[44px]"
          >
            Usar avatar <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
